import React, { useCallback, useEffect, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker, useMapsLibrary, useMap } from '@vis.gl/react-google-maps';

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const DUBAI = { lat: 25.2048, lng: 55.2708 };

/**
 * Where the unit is, for a lead we are turning away over the area.
 *
 * The same two ways of answering as Add Property, because the office already
 * knows them: search for the building by name, or tap the map. An area name
 * groups the rejections; only a pin says whether four "JVC" leads are on one
 * street or spread across the district — which is the actual question when
 * deciding where to extend a route.
 */
export default function LeadLocationPicker({ value, onChange }) {
    // A blank field is not a coordinate. The form carries '' for "not set", and
    // `'' != null` is true while `Number('')` is 0 — so an unpinned lead was
    // dropping a marker in the Gulf of Guinea and calling it pinned.
    const num = (v) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v))
        ? null : Number(v));
    const lat = num(value?.latitude);
    const lng = num(value?.longitude);
    const position = (lat !== null && lng !== null) ? { lat, lng } : null;

    const pick = useCallback((p) => {
        onChange({
            latitude: p.lat,
            longitude: p.lng,
            place_name: p.placeDesc ?? value?.place_name ?? '',
            maps_url: p.mapsUri ?? value?.maps_url ?? '',
        });
    }, [onChange, value?.place_name, value?.maps_url]);

    if (!GMAPS_KEY) {
        return (
            <p className="lds__err">
                The map cannot load — REACT_APP_GOOGLE_MAPS_KEY is not set in this build.
            </p>
        );
    }

    return (
        <APIProvider apiKey={GMAPS_KEY} libraries={['places']}>
            <div className="lds__map">
                <PlacesSearch onPick={pick} initialText={value?.place_name || ''} />
                <div className="lds__mapCanvas">
                    <Map
                        mapId={MAP_ID}
                        defaultCenter={position || DUBAI}
                        defaultZoom={position ? 16 : 10}
                        disableDefaultUI
                        zoomControl
                        gestureHandling="greedy"
                    >
                        {position && <PanTo position={position} />}
                        {position && <AdvancedMarker position={position} />}
                        <MapClick onPick={pick} />
                    </Map>
                </div>
                <p className="lds__fieldNote">
                    {position
                        ? `Pinned${value.place_name ? ` — ${value.place_name}` : ''} `
                          + `(${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}). Tap the map to move it.`
                        : 'Search for the building, or tap the map to drop a pin.'}
                </p>
            </div>
        </APIProvider>
    );
}

function PlacesSearch({ onPick, initialText }) {
    const placesLib = useMapsLibrary('places');
    const hostRef = useRef(null);
    const elRef = useRef(null);

    useEffect(() => {
        if (!placesLib || !hostRef.current || elRef.current) return undefined;
        const host = hostRef.current;
        const el = new window.google.maps.places.PlaceAutocompleteElement();
        try { el.includedRegionCodes = ['ae']; } catch { /* older SDKs ignore it */ }
        el.placeholder = 'Search building or address';
        // What the office already called this place — "Opalz by Danube",
        // "Alexis Tower" — so the search starts from their words rather than
        // from nothing. Recovered by 2026_08_lead_place_names.sql.
        if (initialText) { try { el.value = initialText; } catch { /* older SDKs */ } }
        el.style.width = '100%';
        el.style.height = '38px';
        el.style.border = '1px solid #d1d5db';
        el.style.borderRadius = '8px';
        el.style.background = '#fff';

        const onSelect = async (evt) => {
            const pp = evt?.placePrediction;
            if (!pp) return;
            const place = pp.toPlace();
            await place.fetchFields({ fields: ['id', 'location', 'displayName', 'googleMapsLinks'] });
            const loc = place.location;
            if (!loc) return;
            onPick({
                lat: loc.lat(),
                lng: loc.lng(),
                placeDesc: place.displayName || '',
                mapsUri: place.googleMapsLinks?.placeURI || null,
            });
        };

        el.addEventListener('gmp-select', onSelect);
        host.appendChild(el);
        elRef.current = el;
        return () => {
            try {
                el.removeEventListener('gmp-select', onSelect);
                if (host.contains(el)) host.removeChild(el);
            } catch { /* already detached */ }
            elRef.current = null;
        };
        // initialText is read once, on mount: re-applying it would fight the
        // person typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placesLib, onPick]);

    return <div ref={hostRef} className="lds__mapSearch" />;
}

function MapClick({ onPick }) {
    const map = useMap();
    useEffect(() => {
        if (!map) return undefined;
        const listener = map.addListener('click', (e) => {
            const lat = e.latLng?.lat?.();
            const lng = e.latLng?.lng?.();
            if (typeof lat === 'number' && typeof lng === 'number') onPick({ lat, lng });
        });
        return () => listener?.remove?.();
    }, [map, onPick]);
    return null;
}

function PanTo({ position }) {
    const map = useMap();
    useEffect(() => {
        if (!map || !position) return;
        map.panTo(position);
        map.setZoom(16);
    }, [map, position]);
    return null;
}
